import activeClientMixin from "@/mixins/activeClientMixin";

/**
 * Tracks boolean client fields for the client named by `clientIdKey` (any data, prop or
 * computed on the component; the dropdown's `globalClientId` by default).
 *
 * `fields` maps each data key to the client column it reads, e.g.
 * `{ clientHasInformTenant: "informTenantId" }`. Each key reads false until the lookup
 * resolves, when it fails, while a newly named client is still being looked up, and when
 * the client id is null. All fields share one lookup, so an uncached client is fetched once.
 * `fresh` always fetches, for fields that can change after the dropdown was cached.
 *
 * @param {Object<string, string>} fields data key → client field
 * @param {{ clientIdKey?: string, fresh?: boolean }} [options]
 */
export default function clientFieldsMixin(
  fields,
  { clientIdKey = "globalClientId", fresh = false } = {}
) {
  const keys = Object.keys(fields);
  return {
    mixins: [activeClientMixin],
    data() {
      return Object.fromEntries(keys.map((key) => [key, false]));
    },
    watch: {
      [clientIdKey]: {
        immediate: true,
        async handler(clientId) {
          keys.forEach((key) => (this[key] = false));
          const values = await this.getClientFields(Object.values(fields), clientId, { fresh });
          // Drop a stale answer if the client moved on while this was in flight.
          if (clientId !== this[clientIdKey]) return;
          keys.forEach((key) => (this[key] = !!values[fields[key]]));
        },
      },
    },
  };
}
